import React, { useState } from 'react';
import { getQueueEntry, markAsProceeded } from '../services/queueService';
import '../styles/queueCheckPage.css';

const QueueCheckPage = () => {
  const [queueNumber, setQueueNumber] = useState('');
  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [marking, setMarking] = useState(false);

  const handleCheckQueue = async (e) => {
    e.preventDefault();
    if (!queueNumber.trim()) {
      setError('Please enter a queue number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setQueueData(null);

      const queue = await getQueueEntry(queueNumber.toUpperCase());

      if (!queue) {
        setError('Queue number not found. Please check and try again.');
        return;
      }

      setQueueData(queue);
      setSuccess('Queue information retrieved successfully');
    } catch (err) {
      console.error('Error checking queue:', err);
      setError('Error checking queue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsProceeded = async () => {
    try {
      setMarking(true);
      setError('');
      
      await markAsProceeded(queueNumber.toUpperCase());
      
      // Refresh queue data
      const updated = await getQueueEntry(queueNumber.toUpperCase());
      setQueueData(updated);
      
      setSuccess('Your status has been updated to "Proceeded". Thank you for visiting the PEIS office!');
    } catch (err) {
      console.error('Error marking as proceeded:', err);
      setError('Error updating status. Please try again.');
    } finally {
      setMarking(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#FF9800';
      case 'proceeded':
        return '#07eea9';
      case 'completed':
        return '#4CAF50';
      default:
        return '#999';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending - Awaiting your visit';
      case 'proceeded':
        return 'Proceeded - Completed at office';
      case 'completed':
        return 'Completed - All done!';
      default:
        return 'Unknown';
    }
  };

  const getQueueType = (queueNumber) => {
    if (queueNumber.startsWith('JA')) return 'Job Application';
    if (queueNumber.startsWith('JR')) return 'Job Request';
    return 'Unknown';
  };

  return (
    <div className="queue-check-container extra-space obj-width">
      <div className="queue-check-wrapper">
        <h1>Check Your Queue Status</h1>
        <p className="subtitle">Enter your queue number to check your status</p>

        <form onSubmit={handleCheckQueue} className="queue-form">
          <div className="form-group">
            <label htmlFor="queue-number">Queue Number</label>
            <div className="input-wrapper">
              <input
                id="queue-number"
                type="text"
                placeholder="e.g., JA-20260325-ABC123XYZ"
                value={queueNumber}
                onChange={(e) => setQueueNumber(e.target.value.toUpperCase())}
                className="queue-input"
                disabled={loading}
              />
              <i className="bx bx-search"></i>
            </div>
            <small>Format: JA-YYYYMMDD-XXXXX (Job Application) or JR-YYYYMMDD-XXXXX (Job Request)</small>
          </div>

          <button type="submit" className="check-btn" disabled={loading}>
            {loading ? (
              <>
                <i className="bx bx-loader-alt spin"></i>
                Checking...
              </>
            ) : (
              <>
                <i className="bx bx-search"></i>
                Check Status
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="alert alert-error">
            <i className="bx bx-x-circle"></i>
            {error}
            <button onClick={() => setError('')} className="close-alert">&times;</button>
          </div>
        )}

        {success && !queueData && (
          <div className="alert alert-success">
            <i className="bx bx-check-circle"></i>
            {success}
            <button onClick={() => setSuccess('')} className="close-alert">&times;</button>
          </div>
        )}

        {queueData && (
          <div className="queue-result">
            <div className="result-header">
              <h2>Your Queue Information</h2>
              <div className="queue-number-badge">{queueData.queueNumber}</div>
            </div>

            <div className="queue-details">
              <div className="detail-card">
                <div className="detail-label">Type</div>
                <div className="detail-value">{getQueueType(queueData.queueNumber)}</div>
              </div>

              {queueData.jobTitle && (
                <div className="detail-card">
                  <div className="detail-label">Job Title</div>
                  <div className="detail-value">{queueData.jobTitle}</div>
                </div>
              )}

              {queueData.requestTitle && (
                <div className="detail-card">
                  <div className="detail-label">Request Title</div>
                  <div className="detail-value">{queueData.requestTitle}</div>
                </div>
              )}

              <div className="detail-card">
                <div className="detail-label">Created Date</div>
                <div className="detail-value">
                  {new Date(queueData.createdAt).toLocaleDateString()} at{' '}
                  {new Date(queueData.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>

              <div className="status-card">
                <div className="status-indicator" style={{ backgroundColor: getStatusColor(queueData.status) }}></div>
                <div className="status-content">
                  <div className="status-label">Current Status</div>
                  <div className="status-value">{getStatusLabel(queueData.status)}</div>
                </div>
              </div>

              {queueData.status === 'pending' && (
                <div className="info-box pending">
                  <i className="bx bx-info-circle"></i>
                  <div>
                    <strong>Next Step:</strong>
                    <p>Please proceed to your nearest PEIS office with this queue number to complete your application/request process.</p>
                  </div>
                </div>
              )}

              {queueData.status === 'proceeded' && (
                <div className="info-box completed">
                  <i className="bx bx-check-circle"></i>
                  <div>
                    <strong>Status Updated:</strong>
                    <p>Your office visit was recorded on {new Date(queueData.proceedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
            </div>

            {queueData.status === 'pending' && (
              <button
                className="proceed-btn"
                onClick={handleMarkAsProceeded}
                disabled={marking}
              >
                {marking ? (
                  <>
                    <i className="bx bx-loader-alt spin"></i>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="bx bx-check"></i>
                    Mark as Proceeded to Office
                  </>
                )}
              </button>
            )}

            {success && queueData && (
              <div className="alert alert-success">
                <i className="bx bx-check-circle"></i>
                {success}
                <button onClick={() => setSuccess('')} className="close-alert">&times;</button>
              </div>
            )}
          </div>
        )}

        <div className="info-section">
          <h3>How it Works</h3>
          <ol>
            <li>Your job application or request has been accepted</li>
            <li>You receive a queue number via email</li>
            <li>Use this page to check your queue status anytime</li>
            <li>Visit your nearest PEIS office with your queue number</li>
            <li>Staff will process your application/request</li>
            <li>Mark your visit as completed on this page</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default QueueCheckPage;
